import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Youtube } from "lucide-react";

const YT_URL = "https://www.youtube.com/@thespiritualseekerchannel";

const links = [
    { to: "/", label: "Home", end: true },
    { to: "/videos", label: "Videos" },
    { to: "/calendar", label: "Calendar" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
                scrolled
                    ? "bg-[#FDFBF7]/80 backdrop-blur-xl border-b border-tattva-ornament"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 flex items-center justify-between h-20">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-baseline gap-2 group"
                >
                    <span className="font-serif text-3xl tracking-tight text-tattva-ink group-hover:text-tattva-terracotta transition-colors">
                        Tattva
                    </span>
                    <span className="font-display text-lg text-tattva-terracotta translate-y-0.5">
                        तत्त्व
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-[13px] uppercase tracking-[0.22em] transition-colors ${
                                    isActive
                                        ? "text-tattva-terracotta"
                                        : "text-tattva-ink hover:text-tattva-terracotta"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <a
                        href={YT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                            // Bullet-proof across iframes / preview shells
                            e.preventDefault();
                            window.open(YT_URL, "_blank", "noopener,noreferrer");
                        }}
                        data-testid="nav-youtube-link"
                        aria-label="The Spiritual Seeker on YouTube"
                        className="flex items-center gap-2 text-tattva-ink hover:text-tattva-terracotta transition-colors cursor-pointer"
                    >
                        <Youtube size={18} strokeWidth={1.6} />
                        <span className="text-[11px] uppercase tracking-[0.22em]">
                            YouTube
                        </span>
                    </a>
                </nav>

                <button
                    data-testid="nav-mobile-toggle"
                    className="md:hidden text-tattva-ink"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    className="md:hidden bg-[#FDFBF7] border-t border-tattva-ornament"
                    data-testid="nav-mobile-panel"
                >
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `text-sm uppercase tracking-[0.22em] ${
                                        isActive
                                            ? "text-tattva-terracotta"
                                            : "text-tattva-ink"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <a
                            href={YT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                e.preventDefault();
                                window.open(
                                    YT_URL,
                                    "_blank",
                                    "noopener,noreferrer",
                                );
                                setOpen(false);
                            }}
                            data-testid="nav-mobile-youtube-link"
                            className="flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-tattva-ink"
                        >
                            <Youtube size={18} strokeWidth={1.6} /> YouTube
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
